import { FC } from "react";
import { ClassNamesArg, css, cx } from "@/domains/emotion";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { mdsColors } from "@/design-system/foundations";
import { MdsIconStylingProps } from "@/design-system/components/icon/MdsIcon";
import { Uuid } from "@/domains/global/identifiers";
import { useTheme } from "@emotion/react";
import { useThemedCollectionColors } from "@/mds/themes/useThemedCollectionColors";

export interface CollectionIconStylingProps {
  className?: ClassNamesArg;
  innerStyles?: {
    MdsIcon?: MdsIconStylingProps;
  };
}

interface CollectionIconProps extends CollectionIconStylingProps {
  collectionId?: Uuid;
  size?: number;
  variant?: "default" | "placeholder";
  legacy?: boolean;
}

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const CollectionIcon: FC<CollectionIconProps> = ({
  collectionId,
  className,
  innerStyles,
  size = 32,
  variant = "default",
  legacy = true,
}) => {
  const theme = useTheme();

  const collectionIconWrapperStyles = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: size,
    width: size,
    flexShrink: 0,
    border: variant === "placeholder" ? `1px dashed ${mdsColors().grey.x400}` : "none",
  });

  const { iconColor } = useThemedCollectionColors(collectionId);
  const collectionBackgroundColor = variant === "placeholder" ? "transparent" : iconColor;

  const collectionIconStyles = css({
    color: variant === "placeholder" ? theme.colors.icon.primary : collectionBackgroundColor,
    fontSize: legacy ? size / 2 : size,
    height: legacy ? size / 2 : size,
    width: legacy ? size / 2 : size,
  });

  return (
    <div className={cx(collectionIconWrapperStyles, className)}>
      <MdsIcon
        kind={MdsIconKind.Collection}
        className={innerStyles?.MdsIcon?.className}
        innerStyles={{
          Icon: {
            className: cx(collectionIconStyles, innerStyles?.MdsIcon?.innerStyles?.Icon?.className),
          },
        }}
      />
    </div>
  );
};
